import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Button, Result, Spin } from 'antd';
import { apiClient } from '../services/apiClient';
import { getApiErrorMessage } from '../utils/apiErrors';
import { loginHrefWithNext, POST_REGISTER_PATH } from '../utils/onboardingNavigation';

/**
 * Completes email verification via GET /auth/verify-email?token=...
 * (ISSUE-242). Public route; no session is required before verification.
 * Continues into sign-in → MFA → prospector when a verify-link was used.
 * Prefer MFA-subsumed signup (no link) when email OTP MFA is required globally.
 */
export const VerifyEmailPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const continueHref = loginHrefWithNext(POST_REGISTER_PATH, { registered: true });

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Missing verification token. Open the link from your email.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await apiClient.get('/auth/verify-email', { params: { token } });
        if (!cancelled) setStatus('ok');
      } catch (e) {
        if (!cancelled) {
          setStatus('error');
          setMessage(getApiErrorMessage(e, 'Verification failed'));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div style={{ maxWidth: 520, margin: '48px auto', padding: 24 }}>
      <Card>
        {status === 'loading' && (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Verifying your email…</p>
          </div>
        )}
        {status === 'ok' && (
          <Result
            status="success"
            title="Email verified"
            subTitle="Sign in once to continue to company intake. You will enter one email security code after your password."
            extra={
              <Button type="primary" onClick={() => navigate(continueHref, { replace: true })}>
                Continue to sign in
              </Button>
            }
          />
        )}
        {status === 'error' && (
          <Result
            status="error"
            title="Could not verify email"
            subTitle={message}
            extra={
              <Button type="primary" onClick={() => navigate(continueHref)}>
                Go to sign in
              </Button>
            }
          />
        )}
      </Card>
    </div>
  );
};
